import { useCallback, useRef } from "react";
import { listSessions, streamMessage } from "./chatApi";
import { useChatStore } from "./chatStore";
import type { SSEEvent } from "./types";

/**
 * Hook that manages sending a message and streaming the SSE response
 * into the Zustand store. Handles all 6 backend event types including
 * tool_start, tool_result, and confirmation flow.
 */
export function useChatStream() {
  const abortRef = useRef<AbortController | null>(null);

  const cancel = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
  }, []);

  const sendMessage = useCallback(
    async (sessionId: string, message: string, confirmedToolCalls?: string[]) => {
      cancel();
      const controller = new AbortController();
      abortRef.current = controller;

      const store = useChatStore.getState();

      // Only add user message + assistant placeholder if this is NOT a confirmation resend
      if (!confirmedToolCalls?.length) {
        store.addMessage({ localId: crypto.randomUUID(), role: "user", content: message });
        store.addMessage({ localId: crypto.randomUUID(), role: "assistant", content: "", streaming: true });
      }
      store.setStreaming(true);
      store.setStreamingPhase("streaming");
      store.setError(null);
      store.clearToolState();

      try {
        const reader = await streamMessage(sessionId, message, controller.signal, confirmedToolCalls);
        let buffer = "";

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += value;
          const parts = buffer.split("\n\n");
          buffer = parts.pop() ?? "";

          for (const part of parts) {
            for (const line of part.split("\n")) {
              if (!line.startsWith("data: ")) continue;
              const json = line.slice(6).trim();
              if (!json || json === "[DONE]") continue;

              let event: SSEEvent;
              try {
                event = JSON.parse(json);
              } catch {
                continue;
              }

              const s = useChatStore.getState();
              switch (event.type) {
                case "token":
                  s.appendToLastMessage(event.delta);
                  break;
                case "tool_start":
                  s.addToolCall({ name: event.name, input: event.input });
                  break;
                case "tool_result":
                  s.addToolResult({ name: event.name, output: event.output });
                  break;
                case "card":
                  s.addCard(event.card);
                  break;
                case "confirmation":
                  s.setConfirmationRequest({
                    tool_name: event.tool_name,
                    tool_id: event.tool_id,
                    input: event.input,
                  });
                  s.setStreamingPhase("awaiting_confirmation");
                  s.setStreaming(false);
                  return; // Stop reading — wait for user confirm/reject
                case "done":
                  break;
                case "error":
                  s.setError(event.message);
                  break;
              }
            }
          }
        }
      } catch (err: unknown) {
        if ((err as Error).name === "AbortError") return;
        useChatStore.getState().setError(
          err instanceof Error ? err.message : "Failed to send message",
        );
      } finally {
        const s = useChatStore.getState();
        s.setStreaming(false);
        s.setStreamingPhase("idle");
        // Mark last assistant message as no longer streaming
        const msgs = s.messages;
        if (msgs.length > 0 && msgs[msgs.length - 1].role === "assistant") {
          const updated = [...msgs];
          updated[updated.length - 1] = { ...updated[updated.length - 1], streaming: false };
          s.setMessages(updated);
        }
        // Refresh session list so sidebar shows updated timestamps
        listSessions()
          .then((data) => useChatStore.getState().setSessions(data.results))
          .catch(() => {});
      }
    },
    [cancel],
  );

  return { sendMessage, cancel };
}
